"use client";

import PlaceholderImage from "@/app/assets/images/img/placeholder.png";
import PlusIcon from "@/app/assets/images/svg/plus.svg";
import TickIcon from "@/app/assets/images/svg/tick.svg";
import mapProductOptions from "@/hooks/mapProductOptions";
import { ProductData } from "@/lib/5874/types";
import Pricing from "@/components/ProductCard/Pricing/Pricing";
import Button from "@/components/UI/Button/Button";
import IconOnlyButton from "@/components/UI/Button/IconOnlyButton/IconOnlyButton";
import CompressedImage from "@/components/UI/CompressedImage/CompressedImage";
import Skeleton from "@/components/UI/Skeleton/Skeleton";
import clsx from "clsx";
import Link from "@/components/UI/Link/Link";
import React, { useMemo } from "react";
import styles from "../ProductRecommendationCarousel.module.scss";

interface RecommendationCardProps {
	product: ProductData;
	isLoading: boolean;
	region: string;
	lang: string;
	isAddedToCart: boolean;
	isModifyingCart: boolean;
	onAddToCart: (e: React.MouseEvent) => void;
}

const RecommendationCard: React.FC<RecommendationCardProps> = ({
	product,
	isLoading,
	region,
	lang,
	isAddedToCart,
	isModifyingCart,
	onAddToCart,
}) => {
	const thumbnail =
		product.media?.images?.find((image) => image.isThumbnail) ||
		product.media?.images?.[0];

	const productSpecifications = useMemo(
		() => mapProductOptions([product]),
		[product],
	);

	return (
		<article className={clsx([styles.card])}>
			<div className={styles.inner}>
				<CompressedImage
					src={`${
						thumbnail?.url?.thumbnail || PlaceholderImage.src
					}?format=webp`}
					height={80}
					width={80}
					altText={product.name}
					containerClass={styles.image}
					imgLink={`/${region}/${lang}${product.path}`}
					withNextImage={false}
				/>
				<div className={styles.details}>
					{product.brand && (
						<Link
							className={styles.brand}
							href={`/${region}/${lang}${product.brand.path}`}
						>
							{product.brand.name}
						</Link>
					)}
					<Link
						href={`/${region}/${lang}${product.path}`}
						className={styles.name}
					>
						{product.name}
					</Link>
					{isLoading ? (
						<>
							<Skeleton width={200} height={30} />
							<Skeleton width={100} height={24} />
						</>
					) : (
						<>
							{productSpecifications && productSpecifications.length > 0 && (
								<div className={styles.specs}>
									{productSpecifications?.map((spec) => (
										<div key={spec.label} className={styles.spec}>
											<span className={styles.label}>{spec.label}:</span>
											<span className={styles.value}>
												{spec.options[0].label}
											</span>
										</div>
									))}
								</div>
							)}
							{product.productClass.toLowerCase() !== "parent" && (
								<Pricing className={styles.price} prodData={product} />
							)}
						</>
					)}
				</div>
				<div className={styles.ctaDesktop}>
					<IconOnlyButton
						variant='tertiary'
						icon={isAddedToCart ? <TickIcon /> : <PlusIcon />}
						type='button'
						onClick={(e) => {
							e.stopPropagation();
							onAddToCart(e);
						}}
						isLoading={isModifyingCart}
					/>
				</div>
			</div>
			<div className={styles.ctaMobile}>
				<Button
					as='button'
					variant='secondary'
					onClick={(e) => {
						e.stopPropagation();
						onAddToCart(e);
					}}
					isLoading={isModifyingCart}
				>
					Add to cart
				</Button>
			</div>
		</article>
	);
};

export default RecommendationCard;
